import React, { useState } from "react";
import StudentDashboard from "../../../layout/StudentDashboard";
import { useGetBookingsQuery } from "../../../hooks/api/Students/Bookings/useGetBookingsQuery";
import Loader from "../../../components/Loader";
import { Button, DatePicker, Drawer, Input, Select, message } from "antd";
import ChatBox from "./ChatBox";
import { useSendConversationsMutation } from "../../../hooks/api/Students/Conversations/useSendConversationsMutation";
import SessionCard from "./SessionCard";
import { Dayjs } from "dayjs";
import { RefreshCcw } from "lucide-react";
import EmptyState from "../../../components/EmptyState";

const { Option } = Select;

interface Booking {
  _id: string;
  subject: string;
  tutorShipType: string;
  student: string;
  tutor: any;
  date: string;
  timeSlots: string[];
  status: string;
  price: number;
  duration: number;
  notes: string[];
  startUtc: string;
  endUtc: string;
}

const StudentBookings = () => {
  const [status, setStatus] = useState("");
  const [chatVisible, setChatVisible] = useState(false);
  const [tutor, setTutor] = useState("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [messageInput, setMessageInput] = useState("");
  const [selectedId, setSelectedId] = useState("");

  const { data, isLoading } = useGetBookingsQuery({ status });

  const { mutateAsync: sendMessage, isLoading: isSendingMessage } =
    useSendConversationsMutation({
      id: selectedId,
      setMessageInput,
    });

  const handleChatClick = (record: Booking) => {
    setSelectedId(record._id);
    setChatVisible(true);
    setTutor(record?.tutor?.name || "Tutor");
  };

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;
    sendMessage({ message: messageInput, sender: "Student", type: "text" });
  };

  const resetFilters = () => {
    setStatus("");
    setStartDate(null);
    setEndDate(null);
  };

  const bookings = data?.data?.docs || [];
  const total = data?.data?.meta?.totalDocs || 0;

  return (
    <StudentDashboard>
      {isLoading && !data && <Loader />}

      {!isLoading && (
        <>
          <div className="flex md:flex-row flex-col justify-between md:items-center">
            <h3 className="W2STMedium text-[20px] my-4">Bookings ({total})</h3>
            <div className="flex justify-end py-4 gap-4 flex-wrap">
              <DatePicker.RangePicker
                className="w-[200px]"
                onChange={(dates: [Dayjs | null, Dayjs | null] | null) => {
                  if (dates?.[0] && dates?.[1]) {
                    setStartDate(dates[0]);
                    setEndDate(dates[1]);
                  } else {
                    setStartDate(null);
                    setEndDate(null);
                  }
                }}
                value={startDate && endDate ? [startDate, endDate] : null}
              />

              <Select
                placeholder="Select Status"
                onChange={(value) => setStatus(value)}
                style={{ width: 200 }}
                size="large"
                value={status}
              >
                <Option value="">All Bookings</Option>
                <Option value="pending">Pending</Option>
                <Option value="cancelled">Cancelled</Option>
                <Option value="confirmed">Confirmed</Option>
                <Option value="completed">Completed</Option>
              </Select>

              <button
                className="flex items-center gap-2 bg-gray-300 rounded-md px-3"
                onClick={resetFilters}
              >
                <RefreshCcw size={15} />
                <span>Reset</span>
              </button>
            </div>
          </div>

          {bookings.length > 0 ? (
            <div className="grid md:grid-cols-2 grid-cols-1 gap-[10px]">
              {bookings.map((booking: Booking) => (
                <div key={booking._id}>
                  <SessionCard
                    _id={booking._id}
                    subject={booking.subject}
                    tutorShipType={booking.tutorShipType}
                    date={booking.date}
                    tutor={booking.tutor}
                    duration={booking.duration}
                    timeSlots={booking.timeSlots}
                    status={booking.status}
                    price={booking.price}
                    notes={booking.notes}
                    startUtc={booking.startUtc}
                    endUtc={booking.endUtc}
                    onChat={() => handleChatClick(booking)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No Result for this filter" />
          )}
        </>
      )}

      <Drawer
        title={`Chat with ${tutor} right`}
        placement="right"
        width={window.innerWidth > 768 ? 500 : "100%"}
        onClose={() => {
          setChatVisible(false);
          setTutor("");
        }}
        open={chatVisible}
      >
        <div className="flex flex-col h-[calc(100vh-120px)]">
          <div className="flex-1 overflow-y-auto px-1">
            <ChatBox id={selectedId} />
          </div>
          <div className="sticky bottom-0 bg-white pt-4 pb-6 border-t border-gray-100">
            <div className="relative">
              <textarea
                value={messageInput}
                rows={4}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder="Type your message here..."
                disabled={isSendingMessage}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                className="w-full min-h-[80px] max-h-[160px] p-4 pr-[100px] rounded-xl border border-gray-200 focus:border-primary700 focus:ring-1 focus:ring-primary700 resize-y placeholder:text-gray-400"
              />
              <Button
                type="primary"
                size="large"
                onClick={handleSendMessage}
                disabled={isSendingMessage || !messageInput.trim()}
                className="absolute bottom-4 right-4 hover:opacity-90 transition-opacity rounded-lg flex items-center gap-2"
              >
                Send
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </Button>
            </div>

            {/* <Input
              value={messageInput}
              size="large"
              onChange={(e) => setMessageInput(e.target.value)}
              placeholder="Type a message"
              disabled={isSendingMessage}
            />
            <Button
              type="primary"
              onClick={handleSendMessage}
              disabled={isSendingMessage || !messageInput.trim()}
            >
              Send
            </Button> */}
          </div>
        </div>
      </Drawer>
    </StudentDashboard>
  );
};

export default StudentBookings;
